import React, { useState, useEffect, useRef } from "react";
import { Check } from "lucide-react";

interface Option {
  value: string;
  label: string;
}

interface CustomMultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (selectedRoles: string[]) => void;
  placeholder?: string;
  error?: boolean; // this replaces the old borderColor logic
}

const CustomMultiSelect = ({
  options,
  value,
  onChange,
  placeholder,
  error = false,
}: CustomMultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const toggleSelect = () => setIsOpen((prev) => !prev);

  const handleOptionClick = (optionValue: string) => {
    const updatedValues = value.includes(optionValue)
      ? value.filter((item) => item !== optionValue)
      : [...value, optionValue];
    onChange(updatedValues);
  };

  const getSelectedLabels = () => {
    if (value.length === 0) return placeholder || "Select multiple options...";
    return options
      .filter((option) => value.includes(option.value))
      .map((option) => option.label)
      .join(", ");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={selectRef}>
      <button
        type="button"
        className={`relative h-11 w-full rounded-lg border appearance-none px-4 py-2.5 
          text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-none focus:ring bg-transparent
          dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 
          text-gray-800 border-gray-300 dark:border-gray-600 focus:border-brand-300 dark:focus:border-brand-800 focus:ring-brand-500/10  text-left
          ${error
            ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
            : isOpen
              ? "border-blue-300 ring ring-brand-500/10"
              : "border-gray-300"
          }`}
        onClick={toggleSelect}
        aria-expanded={isOpen}
      >
        {getSelectedLabels()}
        <div className="absolute top-1/2 end-3 -translate-y-1/2">
          <svg
            className="w-5 h-5 text-gray-400 dark:text-gray-400"  // Increased from size-3.5 (~14px) to 6 (24px)
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 10l5 5 5-5" /> {/* Always down arrow */}
          </svg>


        </div>
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 w-full mt-2 z-50
            max-h-[96px] overflow-y-auto
            scrollbar-light dark:scrollbar-dark
            p-1 bg-white border border-gray-200 rounded-lg shadow-lg
            dark:bg-gray-900 dark:border-gray-600 dark:text-gray-300"
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={`py-2 px-4 text-sm cursor-pointer rounded-lg flex justify-between items-center
                hover:bg-gray-100 dark:hover:bg-gray-700
                ${value.includes(option.value)
                  ? "text-blue-500 dark:text-blue-400"
                  : ""
                }`}
              onClick={() => handleOptionClick(option.value)}
            >
              {option.label}
              {value.includes(option.value) && (
                <span className="text-blue-500 dark:text-blue-400">
                  <Check size={16} />
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomMultiSelect;
